import { createStore, Provider as JotaiProvider } from 'jotai';
import { useMemo, type ReactNode } from 'react';

import type { CampaignEditorSubmitPayload } from '../types';
import {
  campaignEndDateAtom,
  campaignNameAtom,
  campaignStartDateAtom,
  creatorItemsAtom,
  creatorKeywordsAtom,
  defaultCampaignEditorCreatorItem,
  defaultCampaignEditorPostItem,
  hasEditedCampaignNameAtom,
  hasEndDateAtom,
  postItemsAtom,
} from './campaign-editor-atoms';
import { campaignInfoIssueAtom } from './campaign-editor-issues';

type CampaignEditorProviderProps = {
  children?: ReactNode;
  initialValues?: Partial<CampaignEditorSubmitPayload>;
};

function createCampaignEditorStore(initialValues?: Partial<CampaignEditorSubmitPayload>) {
  const store = createStore();
  const campaignName = initialValues?.campaignName ?? '';

  store.set(campaignNameAtom, campaignName);
  store.set(hasEditedCampaignNameAtom, campaignName.trim().length > 0);
  store.set(campaignStartDateAtom, initialValues?.campaignStartDate ?? ('' as DateISOString));
  store.set(hasEndDateAtom, initialValues?.hasEndDate ?? false);
  store.set(campaignEndDateAtom, initialValues?.campaignEndDate);
  store.set(creatorItemsAtom, initialValues?.creatorItems ?? [defaultCampaignEditorCreatorItem()]);
  store.set(creatorKeywordsAtom, initialValues?.creatorKeywords ?? []);
  store.set(postItemsAtom, initialValues?.postItems ?? [defaultCampaignEditorPostItem()]);
  store.set(campaignInfoIssueAtom, { section: 'campaign-info', reason: 'incomplete' });

  return store;
}

export function CampaignEditorProvider({ children, initialValues }: CampaignEditorProviderProps) {
  const store = useMemo(() => createCampaignEditorStore(initialValues), [initialValues]);

  return <JotaiProvider store={store}>{children}</JotaiProvider>;
}
